import React, { useState, useCallback } from "react";
// DESIGN: The Portal rail is a compact dark-console control surface. Metrics is
// framed as a subscriber-safe outcome view, never a raw substrate cost/Fires tool.
// DESIGN: The Portal rail remains a compact dark-console control surface.
// MCP Connect is a subscriber-safe guidance destination, not an operator tool.
import {
  Activity, Cable, Code2, CreditCard, DollarSign, GitBranch, Layers, LayoutDashboard,
  Lock, MessageSquare, Plus, ScrollText, Settings, Shield, ShieldCheck, User,
  Wand2, BookOpen, Package, Zap, Trash2, X, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown,
} from "lucide-react";
import api from "../../api";
import type { ProjectSummary, HealthResponse } from "../../api";
import { useApi } from "../../useApi";
import { useOnboarding } from "../../context/OnboardingContext";
import type { OnboardingTier } from "../../context/OnboardingContext";
import { useAuth } from "../../context/AuthContext";
import type { UserTier } from "../../context/AuthContext";
import { isPageAllowedForTier } from "../../app/tier-visibility";
import { Dot } from "../shared/Dot";
import { projectSyncStatus } from "./projectSyncStatus";

/* ═══════════════════════════════════════════════════════════════ */
/*  NAV CONFIG                                                     */
/* ═══════════════════════════════════════════════════════════════ */

export type NavTier = 1 | 2 | 3 | 4;

export const NAV: Array<{ id: string; label: string; I: any; tier: NavTier }> = [
  { id: "overview", label: "Dashboard", I: LayoutDashboard, tier: 1 },
  { id: "subscriber-chat", label: "Chat", I: MessageSquare, tier: 1 },
  { id: "mcp-connect", label: "MCP Connect", I: Cable, tier: 1 },
  { id: "runs", label: "Pipelines", I: GitBranch, tier: 2 },
  { id: "logs", label: "Logs", I: ScrollText, tier: 2 },
  { id: "sync", label: "Sync", I: Wand2, tier: 3 },
  { id: "journal", label: "Journal", I: BookOpen, tier: 3 },
  { id: "guardian", label: "Guardian", I: ShieldCheck, tier: 3 },
  { id: "intel", label: "Intel", I: AlertTriangle, tier: 3 },
  { id: "skills", label: "Skills", I: Code2, tier: 3 },
  { id: "modules", label: "Modules", I: Package, tier: 2 },
  { id: "infra", label: "Infrastructure", I: Layers, tier: 4 },
  { id: "metrics", label: "Metrics", I: Activity, tier: 1 },
  { id: "costs", label: "Costs", I: DollarSign, tier: 4 },
  { id: "safety", label: "Safety", I: Shield, tier: 4 },
  { id: "credentials", label: "Credentials", I: Lock, tier: 2 },
  { id: "config", label: "Config", I: Zap, tier: 1 },
  { id: "subscription", label: "Subscription", I: CreditCard, tier: 1 },
  { id: "profile", label: "My profile", I: User, tier: 1 },
  { id: "settings", label: "Settings", I: Settings, tier: 2 },
];

// Topbar nav items, tier-filtered
export const TOPBAR_NAV: Array<{ id: string; label: string; tier: NavTier }> = [
  { id: "overview", label: "dashboard", tier: 1 },
  { id: "subscriber-chat", label: "chat", tier: 1 },
  { id: "runs", label: "pipelines", tier: 2 },
  { id: "logs", label: "logs", tier: 2 },
  { id: "infra", label: "infrastructure", tier: 4 },
  { id: "costs", label: "costs", tier: 4 },
];

// Static helper (can't use hook outside component)
export function tierToMaxStatic(tier: OnboardingTier): NavTier {
  switch (tier) {
    case 'new_user': case 'has_project': return 1;
    case 'has_first_sync': return 2;
    case 'has_first_run': return 3;
    case 'graduated': return 4;
  }
}

/* ═══════════════════════════════════════════════════════════════ */
/*  SIDEBAR                                                       */
/* ═══════════════════════════════════════════════════════════════ */

function Sidebar({ page, go, projects, health, onRemoveProject, maxTier = 4, userTier = 'developer', mobileOpen = false, collapsed = false, onToggle }: {
  page: string; go: (p: string) => void; projects: ProjectSummary[]; health: HealthResponse | null;
  onRemoveProject?: (id: string) => void;
  maxTier?: NavTier;
  userTier?: UserTier;
  mobileOpen?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  // Two gates: onboarding progressive disclosure (maxTier) AND the S182
  // user_tier persona gate (builder sees the minimal set; developer = full).
  const visibleNav = NAV.filter((n: { id: string; tier: NavTier }) => n.tier <= maxTier && isPageAllowedForTier(n.id, userTier));
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>({});
  const [projectChats, setProjectChats] = useState<Record<string, Array<{ sessionId: string; chatSessionId: string; title: string }>>>({});
  const { data: pendingActions } = useApi(() => api.getPendingActions(), [projects.length]);

  const loadChats = useCallback(async (projectId: string) => {
    try {
      const response = await api.getSubscriberChats(projectId);
      setProjectChats((old) => ({ ...old, [projectId]: response.chats ?? [] }));
    } catch { setProjectChats((old) => ({ ...old, [projectId]: [] })); }
  }, []);
  const toggleProject = (projectId: string) => {
    setOpenProjects((old) => { const next = !old[projectId]; if (next) void loadChats(projectId); return { ...old, [projectId]: next }; });
  };
  const newChat = async (projectId: string) => {
    try { const response = await api.createSubscriberChat(projectId); await loadChats(projectId); go(`subscriber-chat:chat:${response.chatSessionId}`); } catch { /* keep sidebar usable on failure */ }
  };

  const handleRemove = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmRemove === projectId) {
      setRemoving(true);
      onRemoveProject?.(projectId);
      setConfirmRemove(null);
      setRemoving(false);
    } else {
      setConfirmRemove(projectId);
      // Auto-dismiss after 3s
      setTimeout(() => setConfirmRemove((prev: string | null) => prev === projectId ? null : prev), 3000);
    }
  };

  if (collapsed) {
    return (
      <aside className={`sidebar sidebar--collapsed${mobileOpen ? ' mobile-open' : ''}`}>
        <button
          className="sidebar-collapse-btn"
          onClick={onToggle}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          type="button"
        >
          <ChevronRight size={14} />
        </button>
      </aside>
    );
  }

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      {/* Brand + collapse toggle */}
      <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  </div>
        {onToggle && (
          <button
            className="sidebar-collapse-btn"
            onClick={onToggle}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            type="button"
            style={{ border: 'none', width: 24, height: 24, flexShrink: 0 }}
          >
            <ChevronLeft size={14} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "var(--space-2) 0" }}>
        <div className="sidebar-section-label">Workspace</div>
        {visibleNav.map(({ id, label, I }: { id: string; label: string; I: any }) => (
          <div
            key={id}
            className={`sidebar-item ${page === id ? "active" : ""}`}
            onClick={() => go(id)}
          >
            <I size={14} strokeWidth={page === id ? 2 : 1.5} />
            {label}
          </div>
        ))}

        <div className="sidebar-divider" />

        <div className="sidebar-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <span>Projects ({projects.length})</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {/* ocn-S818-P0 Part 2: every "add project" affordance routes to
                the canonical three-card entry screen, never the legacy
                addproject/wizard pages. */}
            <button onClick={() => go('new-project')} type="button" title="Add project" aria-label="Add project" style={{ display: 'inline-flex', alignItems: 'center', padding: 0, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <Plus size={14} />
            </button>
            <button onClick={() => setProjectsExpanded((expanded) => !expanded)} type="button" title={projectsExpanded ? 'Collapse projects' : 'Expand projects'} aria-label={projectsExpanded ? 'Collapse projects' : 'Expand projects'} aria-expanded={projectsExpanded} style={{ display: 'inline-flex', alignItems: 'center', padding: 0, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}>
              {projectsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </span>
        </div>
        {projectsExpanded && projects.map((p: ProjectSummary) => {
          const syncStatus = projectSyncStatus(p.project_id, pendingActions ?? []);
          return (
          <React.Fragment key={p.project_id}>
          <div
            className={`sidebar-project ${page.startsWith(`subscriber-chat:chat:`) ? "" : page === `subscriber-chat:project:${p.project_id}` ? "active" : ""}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 8 }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
              <button onClick={() => toggleProject(p.project_id)} aria-label="Toggle chats" style={{ border: 0, background: 'none', padding: 0, display: 'inline-flex', cursor: 'pointer' }}>{openProjects[p.project_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>
              <span
                title={syncStatus === "failed" ? "The latest sync of this project failed. Open the project for details." : syncStatus === "review" ? "This project has actions waiting for your review." : "Healthy"}
                style={{ display: 'inline-flex', flexShrink: 0 }}
              >
                <Dot status={syncStatus === "review" ? "warning" : syncStatus} sz={8} />
              </span>
              <button onClick={() => go(`project:${p.project_id}`)} title={p.display_name} style={{ border: 0, background: 'none', padding: 0, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>{p.display_name}</button>
            </span>
            <button onClick={() => void newChat(p.project_id)} title="New chat" aria-label={`New chat in ${p.display_name}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><Plus size={13} /></button>
            <button
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); go(`project:${p.project_id}`); }}
              title="Open project details" aria-label={`Open ${p.display_name} details`}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'inline-flex' }}
            ><ChevronRight size={14} /></button>
            {onRemoveProject && (
              <button
                onClick={(e: React.MouseEvent) => handleRemove(p.project_id, e)}
                disabled={removing}
                title={confirmRemove === p.project_id ? 'Click again to confirm removal' : 'Remove project'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                  color: confirmRemove === p.project_id ? 'var(--red)' : 'var(--text-ghost)',
                  opacity: confirmRemove === p.project_id ? 1 : 0,
                  transition: 'opacity 0.15s, color 0.15s',
                  flexShrink: 0,
                }}
                className="sidebar-remove-btn"
              >
                {confirmRemove === p.project_id ? <X size={12} /> : <Trash2 size={11} />}
              </button>
            )}
          </div>
          {openProjects[p.project_id] && (projectChats[p.project_id] ?? []).map((chat) => <button key={chat.sessionId} onClick={() => go(`subscriber-chat:chat:${chat.chatSessionId}`)} className={page === `subscriber-chat:chat:${chat.chatSessionId}` ? 'sidebar-project active' : 'sidebar-project'} style={{ marginLeft: 25, width: 'calc(100% - 25px)', textAlign: 'left', border: 0, background: 'transparent', padding: '5px 8px', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chat.title || 'New chat'}</button>)}
          </React.Fragment>
          );
        })}
      </nav>

      {/* Footer status: the full build identity is available on hover, never inline. */}
      <div
        className="sidebar-footer-status"
        title={`Dashboard build ${__BUILD__.commit} (${__BUILD__.builtAt})`}
        aria-label={`API ${health ? 'connected' : 'disconnected'}, version ${health?.version || __APP_VERSION__}`}
      >
        <Dot status={health ? "healthy" : "failed"} sz={11} />
        <span className="sidebar-footer-status__label">
          API
        </span>
        <span className="sidebar-footer-status__version">
          · v{(health?.version || __APP_VERSION__).replace(/^v/, '')}
        </span>
      </div>

    </aside>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  TIER-AWARE WRAPPERS                                            */
/* ═══════════════════════════════════════════════════════════════ */

export function TopbarNav({ page, go }: { page: string; go: (p: string) => void }) {
  const { tier } = useOnboarding();
  const { userTier } = useAuth();
  const maxTier = tierToMaxStatic(tier);
  const visible = TOPBAR_NAV.filter((n: { id: string; tier: NavTier }) => n.tier <= maxTier && isPageAllowedForTier(n.id, userTier));
  return (
    <nav className="topbar-nav">
      {visible.map((n: { id: string; label: string }) => (
        <a
          key={n.id}
          className={`topbar-nav-item ${page === n.id ? "active" : ""}`}
          onClick={() => go(n.id)}
          style={{ cursor: "pointer" }}
        >
          {n.label}
        </a>
      ))}
    </nav>
  );
}

export function SidebarWithTier(props: {
  page: string; go: (p: string) => void; projects: ProjectSummary[]; health: HealthResponse | null;
  onRemoveProject?: (id: string) => void;
  mobileOpen?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const { tier } = useOnboarding();
  const { userTier } = useAuth();
  const maxTier = tierToMaxStatic(tier);
  return <Sidebar {...props} maxTier={maxTier} userTier={userTier} />;
}
